import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import GlobalFilter from "../components/common/filter";
import { url } from "../constants/url";

const ProfessionalsList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfessionals = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(url + "/api/admin/professionals", {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : ""
        }
      });
      if (!response.ok) throw new Error("Failed to fetch professionals");

      const professionals = await response.json();
      const mappedData = professionals.map((pro) => {
        const createdAt = pro.UserInformation?.createdAt;
        return {
          id: pro.id,
          professionalName: pro.fullName || pro.name || "N/A",
          location: pro.UserInformation?.city || "N/A",
          joiningDate: createdAt
            ? new Date(createdAt).toLocaleDateString()
            : "N/A",
          joiningDateRaw: createdAt ? new Date(createdAt).getTime() : 0,
          isSuspended: pro.isSuspended || false,
          reports: pro.reports || 0,
          subscription: Array.isArray(pro.Subscription)
            ? pro.Subscription.length
            : pro.Subscription
            ? 1
            : "N/A"
        };
      });

      setData(mappedData);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  const navigate = useNavigate();
  const handleSuspend = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to suspend this professional?"))
      return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${url}/api/admin/professionals/${id}/suspend`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      // 401 → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to suspend professional");

      await response.json();
      setData((prev) =>
        prev.map((pro) => (pro.id === id ? { ...pro, isSuspended: true } : pro))
      );
    } catch (err) {
      console.error("Error suspending professional:", err);
    }
  }, []);

  const handleUnsuspend = useCallback(async (id) => {
    if (
      !window.confirm("Are you sure you want to unsuspend this professional?")
    )
      return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${url}/api/admin/professionals/${id}/unsuspend`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      // 401 → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to unsuspend professional");

      await response.json();
      setData((prev) =>
        prev.map((pro) =>
          pro.id === id ? { ...pro, isSuspended: false } : pro
        )
      );
    } catch (err) {
      console.error("Error unsuspending professional:", err);
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this professional? This action cannot be undone."
      )
    )
      return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${url}/api/admin/users/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      // 401 → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to delete professional");

      await response.json();
      setData((prev) => prev.filter((pro) => pro.id !== id));
    } catch (err) {
      console.error("Error deleting professional:", err);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Professional Name",
        accessor: "professionalName",
        Cell: ({ row, value }) => (
          <Link
            to={`/professionals-list/${row.original.id}`}
            className="text-blue-500 no-underline"
          >
            {value}
          </Link>
        )
      },
      { Header: "Location", accessor: "location" },
      {
        Header: "Joining Date",
        accessor: "joiningDateRaw",
        Cell: ({ row }) => row.original.joiningDate
      },
      {
        Header: "Status",
        accessor: "isSuspended",
        Cell: ({ value }) => (
          <span
            className={`px-3 py-1 rounded-3xl text-white ${
              value ? "bg-red-500" : "bg-green-500"
            }`}
          >
            {value ? "Suspended" : "Active"}
          </span>
        )
      },
      {
        Header: "Action",
        Cell: ({ row }) =>
          row.original.isSuspended ? (
            <button
              onClick={() => handleUnsuspend(row.original.id)}
              className="text-blue-500 hover:underline"
            >
              Unsuspend
            </button>
          ) : (
            <button
              onClick={() => handleSuspend(row.original.id)}
              className="text-blue-500 hover:underline"
            >
              Suspend
            </button>
          )
      },
      {
        Header: "Delete",
        Cell: ({ row }) => (
          <button
            onClick={() => handleDelete(row.original.id)}
            className="text-red-500 hover:underline"
          >
            Delete
          </button>
        )
      }
    ],
    [handleSuspend, handleDelete, handleUnsuspend]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    preGlobalFilteredRows
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: "joiningDateRaw", desc: true }]
      }
    },
    useGlobalFilter,
    useSortBy
  );

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-3xl shadow-md">
        <h1 className="text-3xl font-semibold mb-4">Professionals List</h1>
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-300 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-red-500">
        Error loading professionals: {error.message}
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-3xl shadow-md">
      <h1 className="text-3xl font-semibold mb-4">Professionals List</h1>
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <div className="overflow-x-auto">
        <table {...getTableProps()} className="min-w-full bg-white mt-4">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="py-3 px-4 border-b text-left"
                  >
                    <div className="flex items-center">
                      {column.render("Header")}
                      <span className="ml-2">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <FaSortDown />
                          ) : (
                            <FaSortUp />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="hover:bg-gray-100">
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className="py-2 px-4 border-b">
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProfessionalsList;
